/**
 * Cognito Hosted UI callback page. Receives `?code=&state=` on redirect
 * from Cognito, validates `state`, exchanges the code for tokens, and
 * hands them to AuthContext before navigating the user to their
 * post-login path.
 *
 * Migrated to Cloudscape (ContentLayout + Box + Spinner + Alert +
 * Button). The OAuth wiring is unchanged.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  ContentLayout,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components";

import { useAuth } from "../auth/AuthContext";
import {
  consumePostLoginPath,
  exchangeCodeForTokens,
  verifyState,
} from "../auth/oauth";

export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { acceptTokens } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = params.get("code");
    const state = params.get("state");
    const cognitoError = params.get("error");
    if (cognitoError) {
      setError(`Cognito returned error: ${cognitoError}`);
      return;
    }
    if (!code || !state) {
      setError("Missing code or state on callback URL.");
      return;
    }
    if (!verifyState(state)) {
      setError("OAuth2 state mismatch; possible CSRF.");
      return;
    }
    let cancelled = false;
    exchangeCodeForTokens(code)
      .then((tokens) => {
        if (cancelled) return;
        acceptTokens(tokens);
        const next = consumePostLoginPath();
        navigate(next, { replace: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [params, acceptTokens, navigate]);

  if (error) {
    return (
      <ContentLayout>
        <SpaceBetween size="m">
          <Alert
            type="error"
            statusIconAriaLabel="Sign-in failed"
            header="Sign-in failed"
          >
            {error}
          </Alert>
          <Button onClick={() => navigate("/", { replace: true })}>
            Return home
          </Button>
        </SpaceBetween>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout>
      <Box textAlign="center" padding="xxl">
        <SpaceBetween size="m" alignItems="center">
          <Spinner size="large" />
          <Box>Completing sign-in&hellip;</Box>
        </SpaceBetween>
      </Box>
    </ContentLayout>
  );
}
